import React, { useEffect } from 'react';
import postalCodes from "postal-codes-js";
import * as Yup from "yup";
import { withFormik } from "formik";

import FormikInput from "../../SharedComponents/Forms/FormikFormGroup/FormikInput";
import FormicCheckbox from "../../SharedComponents/Forms/FormikFormGroup/FormicCheckbox";
import {
  addressValidation,
  cityValidation,
  passwordConfirmationValidation,
  passwordValidation,
  vatIdValidation
} from "../../SharedComponents/Forms/ValidationSchemaPattern";
import {
  emailError,
  postalCodeError,
  requiredFieldError,
  symbolsLimitError
} from "../../SharedComponents/Forms/ErrorMessages";
import ClientStorage from '../../SharedComponents/Forms/ClientStorage';
import { validationPatterns } from "../../SharedComponents/Forms/validationPatterns";
import { EllipsisLoader } from "../../SharedComponents/Loaders";

import './index.sass';


const CompanyFormWrapper = ({ accountType,
                              errors,
                              goBack,
                              handleBlur,
                              handleChange,
                              handleSubmit,
                              loading,
                              paymentType,
                              setErrors,
                              setFieldValue,
                              setSubmitErrors,
                              setValidationTrigger,
                              submitHandler,
                              submitErrors,
                              touched,
                              validationTrigger,
                              values }) => {


  useEffect(() => {
    if (submitErrors) {
      setErrors(submitErrors);
      setSubmitErrors(null);
    }
  }, [submitErrors]);


  useEffect(() => {
    if (validationTrigger) {
      submitHandler(values);
      goBack();
    }
  }, [validationTrigger]);


  useEffect(() => {
    errors && Object.keys(errors).length && validationTrigger && setValidationTrigger(false);
  }, [errors]);


  return (
    <form name="continueRegistrationForm" className="company-form" id="new_provider" onSubmit={handleSubmit}>

      <input
        id="fifth_element"
        type="text"
        name="fifth_element"
        className="invisible"
        value={values.fifth_element}
        onPaste={handleChange}
        onChange={handleChange}
      />

      <FormikInput
        id="address_line_1"
        error={errors.address_line_1}
        handleBlur={handleBlur}
        handleChange={handleChange}
        labelPosition="top"
        placeholder="Adresse*"
        touched={touched.address_line_1}
        value={values.address_line_1}
      />

      <FormikInput
        id="postal_code"
        error={errors.postal_code}
        handleBlur={handleBlur}
        handleChange={handleChange}
        labelPosition="top"
        placeholder="Postnr*"
        touched={touched.postal_code}
        value={values.postal_code}
      />

      <FormikInput
        id="postal_name"
        error={errors.postal_name}
        handleBlur={handleBlur}
        handleChange={handleChange}
        labelPosition="top"
        placeholder="By*"
        touched={touched.postal_name}
        value={values.postal_name}
      />

      <FormikInput
        id="cvr"
        error={errors.cvr}
        handleBlur={handleBlur}
        handleChange={handleChange}
        labelPosition="top"
        placeholder="CVR*"
        touched={touched.cvr}
        value={values.cvr}
      />

      <FormikInput
        id="invoice_email"
        error={errors.invoice_email}
        handleBlur={handleBlur}
        handleChange={handleChange}
        labelPosition="top"
        placeholder="Faktura e-mail*"
        touched={touched.invoice_email}
        value={values.invoice_email}
      />

      <FormikInput
        id="password"
        error={errors.password}
        handleBlur={handleBlur}
        handleChange={(e) => setFieldValue('password', e.target.value.replace(/\s/g, ""))}
        labelPosition="top"
        placeholder="Adgangskode*"
        touched={touched.password}
        type="password"
        value={values.password}
      />

      <FormikInput
        id="password_confirmation"
        error={errors.password_confirmation}
        handleBlur={handleBlur}
        handleChange={(e) => setFieldValue('password_confirmation', e.target.value.replace(/\s/g, ""))}
        labelPosition="top"
        placeholder="Gentag kode*"
        touched={touched.password_confirmation}
        type="password"
        value={values.password_confirmation}
      />

      <FormicCheckbox
        id="terms_of_service"
        error={touched.terms_of_service && errors.terms_of_service}
        handleBlur={handleBlur}
        handleChange={handleChange}
        label={
          <div className="signup-form__terms-label">
            Jeg accepterer <a href={`/terms/${paymentType}/${accountType}.pdf`} target="_blank">betingelserne *</a>
          </div>
        }
        value={values.terms_of_service}
      />

      <button type="submit" className={`signup-form__submit-button ${loading ? 'inactive' : ''}`}>
        { loading ? <EllipsisLoader /> : 'Opret min konto'}
      </button>
    </form>
  )
};

const { postal_codeI18n } = ClientStorage.getNames();

const CompanyForm = withFormik({

  mapPropsToValues: (bag) => ({
    address_line_1: bag.currentValues.address_line_1 || '',
    cvr: bag.currentValues.cvr || '',
    fifth_element: bag.currentValues.fifth_element || '',
    invoice_email: bag.currentValues.invoice_email || '',
    password: bag.currentValues.password || '',
    password_confirmation: bag.currentValues.password_confirmation || '',
    postal_code: bag.currentValues.postal_code || '',
    postal_name: bag.currentValues.postal_name || '',
    terms_of_service: bag.currentValues.terms_of_service || false,
  }),

  validationSchema: Yup.object().shape({
    address_line_1: addressValidation,
    cvr: vatIdValidation,
    fifth_element: Yup.string(),
    invoice_email: Yup.string()
      .matches(validationPatterns.symbolsEmailLimit, symbolsLimitError(100))
      .matches(validationPatterns.emailWithUmlauts, emailError)
      .required(requiredFieldError('E-mail')),
    password: passwordValidation,
    password_confirmation: passwordConfirmationValidation,
    postal_name: cityValidation,
    postal_code: Yup.string()
      .test('isPostalCode', postalCodeError, function(value) {
        const { path, createError } = this;
        const codeIsValid = postalCodes.validate('dk', value);

        return codeIsValid === true || !value || createError({ path, message: postalCodeError });
      })
      .required(requiredFieldError(postal_codeI18n)),
    terms_of_service: Yup.boolean()
      .required('Servicevilkår skal accepteres.')
      .oneOf([true], 'Servicevilkår skal accepteres.'),
  }),

  handleSubmit: (values, bag) => {
    bag.props.submitHandler(values);
  },

  displayName: 'CompanyForm',
})(CompanyFormWrapper);

export default CompanyForm;
